import { Component, forceUpdate, h, Host, Listen, Prop, State, Watch } from '@stencil/core';
import { IntlShape } from '@formatjs/intl';
import { state } from '../../store';
import getAvaappUrl from '../../utils/avaapp-urls';
import getAuiWorkflowUrl from '../../utils/auiworkflow-urls';
import fetchWithRetry, { ResultErrorVariant } from '../../utils/retry';

type SetupStatsResponse = {
  value: {
    value: {
      progressCount?: {
        total?: number;
        completed?: number;
      };
    };
  };
};

@Component({
  tag: 'aui-setup',
  styleUrl: 'aui-setup.scss',
  shadow: false,
})
export class AuiSetup {
  static get FETCH_RETRIES_MAX() {
    return 3;
  }

  static get FETCH_RETRY_DELAY() {
    return 500;
  }

  @Listen('s-intlupdate', { target: 'window' })
  intlUpdateHandler() {
    forceUpdate(this);
  }

  @Prop() expanded: boolean = true;

  @Prop() requestCurrentAccessToken: () => Promise<{ accessToken: string }>;

  @State() intl: IntlShape;

  @Prop({ attribute: 'accountid' }) accountId: string;

  @Prop({ attribute: 'companyid' }) companyId: string;

  @State() completed: number | null = null;

  @State() total: number | null = null;

  @Watch('requestCurrentAccessToken')
  watchRequestCurrentAccessToken() {
    if (this.isReadyToFetch) {
      this.#fetchSetupStats();
    }
  }

  @Watch('accountId')
  watchAccountId() {
    if (this.isReadyToFetch) {
      this.#fetchSetupStats();
    }
  }

  @Watch('companyId')
  watchCompanyId() {
    if (this.isReadyToFetch) {
      this.#fetchSetupStats();
    }
  }

  get isReadyToFetch() {
    return this.requestCurrentAccessToken && this.accountId && this.companyId;
  }

  async #getAccessToken() {
    const json = await this.requestCurrentAccessToken();
    if (!json) {
      throw new Error('Could not get accessToken from server');
    }
    return json.accessToken;
  }

  async #fetchSetupStats() {
    if (!this.isReadyToFetch) {
      return;
    }

    const accessToken = await this.#getAccessToken();

    const url = `${getAuiWorkflowUrl(window.aui.env)}/aui-workflow/onboarding/progress?accountId=${this.accountId}&tenantId=auiWorkflowTenantId&companyId=${this.companyId}`;

    const response = await fetchWithRetry<SetupStatsResponse>({
      url,
      options: {
        headers: {
          Authorization: `Bearer ${accessToken}`,
        },
      },
      retries: AuiSetup.FETCH_RETRIES_MAX,
      retryDelay: AuiSetup.FETCH_RETRY_DELAY,
    });

    if (!response.ok) {
      const { error } = response as ResultErrorVariant;
      console.error('failure to get aui-workflow', error);
      return;
    }

    const { value } = response as SetupStatsResponse;

    this.completed = value.value.progressCount?.completed;
    this.total = value.value.progressCount?.total;
  }

  componentWillUpdate() {
    state.intl
      .then(value => {
        this.intl = value as IntlShape;
      })
      .catch(err => {
        console.error('Error creating intl object:', err);
      });
  }

  #getLocalText = () => {
    const { intl } = this;
    return {
      setup: intl.formatMessage({
        id: 'skylab.auiSetup.setup',
        defaultMessage: 'Setup',
      }),
      stepsComplete: intl.formatMessage(
        {
          id: 'skylab.auiSetup.stepsComplete',
          defaultMessage: '{COMPLETED} of {TOTAL} steps complete',
        },
        { COMPLETED: this.completed, TOTAL: this.total },
      ),
    };
  };

  static handleOnClick = () => {
    window.location.href = `${getAvaappUrl(window.aui.env)}/home/setup`;
  };

  render() {
    // if the intl is not loaded, don't render the component
    if (!this.intl) return null;

    // if either of the completed or total is null/undefined, don't render the component.  Be sure to allow 0.
    if (this.completed == null || this.total == null) {
      return null;
    }

    const { setup, stepsComplete } = this.#getLocalText();
    const percentage = (this.completed / this.total) * 100;

    return (
      <Host>
        {this.expanded && (
          <div class="expanded">
            <button class="ghost full-width" onClick={AuiSetup.handleOnClick}>
              <div class="setup-header">
                <s-icon name="wrench-2" aria-hidden="true"></s-icon>
                <div class="title h4">{setup}</div>
              </div>
              <div class="subtitle text-start text-xs font-normal">{stepsComplete}</div>
              <meter value={percentage} min={0} max={100} optimum={100}></meter>
            </button>
          </div>
        )}
        {!this.expanded && (
          <div class="collapsed">
            <button class="ghost" onClick={AuiSetup.handleOnClick}>
              <div class="meter-icon-wrapper">
                <s-icon name="wrench-2" aria-hidden="true"></s-icon>
                <meter value={percentage} min={0} max={100} optimum={100}></meter>
              </div>
            </button>
          </div>
        )}
        {this.expanded && <hr></hr>}
      </Host>
    );
  }
}
